/**
 * 번역 키 상수 정의
 * 
 * 이 파일은 모든 번역 키를 중앙에서 관리하여
 * 타입 안정성과 일관성을 보장합니다.
 */

// 공통 키
export const COMMON_KEYS = {
  LOADING: 'common.loading',
  ERROR: 'common.error',
  SUCCESS: 'common.success',
  CANCEL: 'common.cancel',
  CONFIRM: 'common.confirm',
  SAVE: 'common.save',
  EDIT: 'common.edit',
  DELETE: 'common.delete',
  SEARCH: 'common.search',
  RESET: 'common.reset',
  CLOSE: 'common.close',
  BACK: 'common.back',
  NEXT: 'common.next',
  PREVIOUS: 'common.previous',
  SUBMIT: 'common.submit',
  DOWNLOAD: 'common.download',
  UPLOAD: 'common.upload',
  APPROVE: 'common.approve',
  REJECT: 'common.reject',
  PENDING: 'common.pending',
  COMPLETED: 'common.completed',
  FAILED: 'common.failed',
  VIEW: 'common.view',
  ACTIONS: 'common.actions',
  TOTAL: 'common.total',
  BYTES: 'common.bytes',
  KB: 'common.kb',
  MB: 'common.mb',
  GB: 'common.gb',
  YESTERDAY: 'common.yesterday',
  DAYS_AGO: 'common.daysAgo',
  WEEKS_AGO: 'common.weeksAgo',
  MONTHS_AGO: 'common.monthsAgo',
  YEARS_AGO: 'common.yearsAgo',
  CREATE: 'common.create',
  UPDATE: 'common.update',
  WARNING: 'common.warning',
  INFO: 'common.info',
  NO_PERMISSION: 'common.noPermission',
  UNKNOWN: 'unknown',
  CREATED_AT: 'common.createdAt',
  UPDATED_AT: 'common.updatedAt',
  VIEW_COUNT: 'common.viewCount',
} as const;

// 인증 관련 키
export const AUTH_KEYS = {
  LOGIN: 'auth.login',
  LOGOUT: 'auth.logout',
  REGISTER: 'auth.register',
  USERNAME: 'auth.username',
  PASSWORD: 'auth.password',
  CONFIRM_PASSWORD: 'auth.confirmPassword',
  EMAIL: 'auth.email',
  ORGAN_NAME: 'auth.organName',
  BUBMYENG: 'auth.bubmyeng',
  WELCOME_MESSAGE: 'auth.welcomeMessage',
  NO_ACCOUNT: 'auth.noAccount',
  REGISTER_TITLE: 'auth.registerTitle',
  HAVE_ACCOUNT: 'auth.haveAccount',
  REGISTER_COMPLETE: 'auth.registerComplete',
  REGISTER_SUCCESS_TITLE: 'auth.registerSuccessTitle',
  ACCOUNT_CREATED: 'auth.accountCreated',
  CAN_LOGIN_NOW: 'auth.canLoginNow',
  LOGIN_SUCCESS: 'auth.loginSuccess',
  LOGOUT_SUCCESS: 'auth.logoutSuccess',
  REGISTER_SUCCESS: 'auth.registerSuccess',
  LOGIN_FAILED: 'auth.loginFailed',
  REGISTER_FAILED: 'auth.registerFailed',
  PASSWORD_MISMATCH: 'auth.passwordMismatch',
  PROFILE_UPDATE_SUCCESS: 'auth.profileUpdateSuccess',
  PROFILE_UPDATE_FAILED: 'auth.profileUpdateFailed',
  PASSWORD_CHANGE_SUCCESS: 'auth.passwordChangeSuccess',
  PASSWORD_CHANGE_FAILED: 'auth.passwordChangeFailed',
  CURRENT_PASSWORD: 'auth.currentPassword',
  NEW_PASSWORD: 'auth.newPassword',
  NEW_PASSWORD_CONFIRM: 'auth.newPasswordConfirm',
} as const;

// 네비게이션 키
export const NAVIGATION_KEYS = {
  TITLE: 'navigation.title',
  SHORT_TITLE: 'navigation.shortTitle',
  HOME: 'navigation.home',
  LAWS: 'navigation.laws',
  ANNOUNCEMENTS: 'navigation.announcements',
  LEGISLATIVE_NOTICES: 'navigation.legislativeNotices',
  PUBLICATIONS: 'navigation.publications',
  MANUALS: 'navigation.manuals',
  BUG_REPORTS: 'navigation.bugReports',
  PROFILE: 'navigation.profile',
  ADMIN: 'navigation.admin',
  USER_MANAGEMENT: 'navigation.userManagement',
  COMMENTS: 'navigation.comments',
} as const;

// 규범류 관련 키
export const LAWS_KEYS = {
  TITLE: 'laws.title',
  LIST: 'laws.list',
  DETAIL: 'laws.detail',
  DESCRIPTION: 'laws.description',
  LOADING: 'laws.loading',
  NOT_FOUND: 'laws.notFound',
  LOAD_FAILED: 'laws.loadFailed',
  APPROVE_SUCCESS: 'laws.approveSuccess',
  APPROVE_CANCEL_SUCCESS: 'laws.approveCancelSuccess',
  APPROVE_FAILED: 'laws.approveFailed',
  DELETE_SUCCESS: 'laws.deleteSuccess',
  DELETE_FAILED: 'laws.deleteFailed',
  DOWNLOAD_FAILED: 'laws.downloadFailed',
  CONFIRM_DELETE: 'laws.confirmDelete',
  APPROVED_DELETE_DISABLED: 'laws.approvedDeleteDisabled',
  CATEGORY: 'laws.category',
  SERIAL_NUMBER: 'laws.serialNumber',
  ATTACHMENTS: 'laws.attachments',
  RESOLUTION_DATE: 'laws.resolutionDate',
  VIEW_COUNT: 'laws.viewCount',
  IS_APPROVED: 'laws.isApproved',
  APPROVED: 'laws.approved',
  UNAPPROVED: 'laws.unapproved',
  APPROVE: 'laws.approve',
  UNAPPROVE: 'laws.unapprove',
  CREATE: 'laws.create',
  SEARCH: 'laws.search',
  KYOHUN: 'laws.kyohun',
  KYOGYU: 'laws.kyogyu',
  KYORYEONG: 'laws.kyoryeong',
  YEGYU: 'laws.yegyu',
  ENACTED: 'laws.enacted',
  AMENDED: 'laws.amended',
  FULLY_AMENDED: 'laws.fullyAmended',
  ABOLISHED: 'laws.abolished',
  SIMPLE_SEARCH: 'laws.simpleSearch',
  FULL_TEXT_SEARCH: 'laws.fullTextSearch',
  ADVANCED_SEARCH: 'laws.advancedSearch',
  ALL: 'laws.all',
  CONTENT: 'laws.content',
  APPROVED_ONLY: 'laws.approvedOnly',
  SERIAL_NUMBER_FORMAT: 'laws.serialNumberFormat',
  DELETE_TITLE: 'laws.deleteTitle',
  SEARCH_FAILED: 'laws.searchFailed',
  TABLE_TITLE: 'laws.tableTitle',
  STATUS: 'laws.status',
  CREATE_SUCCESS: 'laws.createSuccess',
  UPDATE_SUCCESS: 'laws.updateSuccess',
  SAVE_FAILED: 'laws.saveFailed',
  UPLOAD_AFTER_CREATE: 'laws.uploadAfterCreate',
  UPLOAD_SUCCESS: 'laws.uploadSuccess',
  UPLOAD_FAILED: 'laws.uploadFailed',
  DELETE_ATTACHMENT_SUCCESS: 'laws.deleteAttachmentSuccess',
  DELETE_ATTACHMENT_FAILED: 'laws.deleteAttachmentFailed',
  IMAGE_URL_OR_FILE_REQUIRED: 'laws.imageUrlOrFileRequired',
  EDITOR_NOT_FOUND: 'laws.editorNotFound',
  IMAGE_INSERTED: 'laws.imageInserted',
  IMAGE_RESIZED: 'laws.imageResized',
  IMAGE_AUTO_RESIZED: 'laws.imageAutoResized',
  CONTENT_RELOADED: 'laws.contentReloaded',
  CONTENT_RELOAD_HINT: 'laws.contentReloadHint',
  RELOAD_CONTENT: 'laws.reloadContent',
  EDIT_TITLE: 'laws.editTitle',
  CREATE_TITLE: 'laws.createTitle',
  TITLE_REQUIRED: 'laws.titleRequired',
  TITLE_PLACEHOLDER: 'laws.titlePlaceholder',
  CATEGORY_REQUIRED: 'laws.categoryRequired',
  CATEGORY_PLACEHOLDER: 'laws.categoryPlaceholder',
  STATUS_REQUIRED: 'laws.statusRequired',
  STATUS_PLACEHOLDER: 'laws.statusPlaceholder',
  RESOLUTION_DATE_PLACEHOLDER: 'laws.resolutionDatePlaceholder',
  SERIAL_NUMBER_PLACEHOLDER: 'laws.serialNumberPlaceholder',
  CONTENT_REQUIRED: 'laws.contentRequired',
  UPLOAD_ATTACHMENT: 'laws.uploadAttachment',
  UPLOADED: 'laws.uploaded',
  ACTIONS: {
    BACK_TO_LIST: 'laws.actions.backToList',
    EDIT: 'laws.actions.edit',
    AMEND: 'laws.actions.amend',
    APPROVE: 'laws.actions.approve',
    CANCEL_APPROVE: 'laws.actions.cancelApprove',
    DELETE: 'laws.actions.delete',
    DOWNLOAD: 'laws.actions.download',
  },
  DETAIL_SECTION: {
    TITLE: 'laws.detail.title',
    LOADING: 'laws.detail.loading',
    NOT_FOUND: 'laws.detail.notFound',
    BACK_TO_LIST: 'laws.detail.backToList',
    EDIT: 'laws.detail.edit',
    AMEND: 'laws.detail.amend',
    APPROVE: 'laws.detail.approve',
    CANCEL_APPROVE: 'laws.detail.cancelApprove',
    DELETE: 'laws.detail.delete',
    CONFIRM_DELETE: 'laws.detail.confirmDelete',
    APPROVED_DELETE_DISABLED: 'laws.detail.approvedDeleteDisabled',
    DOWNLOAD: 'laws.detail.download',
    ATTACHMENTS: 'laws.detail.attachments',
    CONTENT: 'laws.detail.content',
    LABELS: {
      AUTHOR: 'laws.detail.labels.author',
      CREATED_AT: 'laws.detail.labels.createdAt',
      UPDATED_AT: 'laws.detail.labels.updatedAt',
      DOCUMENT_TYPE: 'laws.detail.labels.documentType',
      SERIAL_NUMBER: 'laws.detail.labels.serialNumber',
      RESOLUTION_DATE: 'laws.detail.labels.resolutionDate',
      CATEGORY: 'laws.detail.labels.category',
      STATUS: 'laws.detail.labels.status',
      VIEW_COUNT: 'laws.detail.labels.viewCount',
      ATTACHMENTS: 'laws.detail.labels.attachments',
      OWNER: 'laws.detail.labels.owner',
    },
  },
  VERSIONS: {
    TITLE: 'laws.versions.title',
    LOADING: 'laws.versions.loading',
    NOT_FOUND: 'laws.versions.notFound',
    COMPARE: 'laws.versions.compare',
    COMPARE_FAILED: 'laws.versions.compareFailed',
    DELETE_SUCCESS: 'laws.versions.deleteSuccess',
    DELETE_FAILED: 'laws.versions.deleteFailed',
    DOWNLOAD_FAILED: 'laws.versions.downloadFailed',
    CURRENT_LAW_LOAD_FAILED: 'laws.versions.currentLawLoadFailed',
    NO_COMPARE_CONTENT: 'laws.versions.noCompareContent',
    DISPLAY: {
      CURRENT_LAW: 'laws.versions.display.currentLaw',
      VERSION_NUMBER: 'laws.versions.display.versionNumber',
    },
    LABELS: {
      VERSION: 'laws.versions.labels.version',
      CREATED_AT: 'laws.versions.labels.createdAt',
      RESOLUTION_DATE: 'laws.versions.labels.resolutionDate',
      STATUS: 'laws.versions.labels.status',
      CONTENT: 'laws.versions.labels.content',
      ATTACHMENTS: 'laws.versions.labels.attachments',
    },
    COMPARE_SECTION: {
      TITLE: 'laws.versions.compare.title',
      PREVIOUS: 'laws.versions.compare.previous',
      CURRENT: 'laws.versions.compare.current',
      CATEGORY_CHANGE: 'laws.versions.compare.categoryChange',
      STATUS_CHANGE: 'laws.versions.compare.statusChange',
      CONTENT_CHANGE: 'laws.versions.compare.contentChange',
      CLOSE: 'laws.versions.compare.close',
    },
  },
  OWNER: 'laws.owner',
  OWNER_PLACEHOLDER: 'laws.ownerPlaceholder',
} as const;

// 공지사항 관련 키
export const ANNOUNCEMENTS_KEYS = {
  TITLE: 'announcements.title',
  LIST: 'announcements.list',
  DETAIL: 'announcements.detail',
  DESCRIPTION: 'announcements.description',
  LOADING: 'announcements.loading',
  NOT_FOUND: 'announcements.notFound',
  LOAD_FAILED: 'announcements.loadFailed',
  CREATE_SUCCESS: 'announcements.createSuccess',
  UPDATE_SUCCESS: 'announcements.updateSuccess',
  DELETE_SUCCESS: 'announcements.deleteSuccess',
  SAVE_FAILED: 'announcements.saveFailed',
  DELETE_FAILED: 'announcements.deleteFailed',
  CONFIRM_DELETE: 'announcements.confirmDelete',
  TITLE_REQUIRED: 'announcements.titleRequired',
  CONTENT_REQUIRED: 'announcements.contentRequired',
  CREATE_TITLE: 'announcements.createTitle',
  EDIT_TITLE: 'announcements.editTitle',
  ACTIONS: {
    BACK_TO_LIST: 'announcements.actions.backToList',
    CREATE: 'announcements.actions.create',
    EDIT: 'announcements.actions.edit',
    DELETE: 'announcements.actions.delete',
  },
  DETAIL_SECTION: {
    TITLE: 'announcements.detail.title',
    LOADING: 'announcements.detail.loading',
    NOT_FOUND: 'announcements.detail.notFound',
    BACK_TO_LIST: 'announcements.detail.backToList',
    EDIT: 'announcements.detail.edit',
    DELETE: 'announcements.detail.delete',
    CONFIRM_DELETE: 'announcements.detail.confirmDelete',
    CONTENT: 'announcements.detail.content',
    LABELS: {
      AUTHOR: 'announcements.detail.labels.author',
      CREATED_AT: 'announcements.detail.labels.createdAt',
      UPDATED_AT: 'announcements.detail.labels.updatedAt',
      VIEW_COUNT: 'announcements.detail.labels.viewCount',
    },
  },
} as const;

// 입법예고 관련 키
export const LEGISLATIVE_NOTICES_KEYS = {
  TITLE: 'legislativeNotices.title',
  LIST: 'legislativeNotices.list',
  DETAIL: 'legislativeNotices.detail',
  DESCRIPTION: 'legislativeNotices.description',
  LOADING: 'legislativeNotices.loading',
  NOT_FOUND: 'legislativeNotices.notFound',
  LOAD_FAILED: 'legislativeNotices.loadFailed',
  CREATE_SUCCESS: 'legislativeNotices.createSuccess',
  UPDATE_SUCCESS: 'legislativeNotices.updateSuccess',
  DELETE_SUCCESS: 'legislativeNotices.deleteSuccess',
  SAVE_FAILED: 'legislativeNotices.saveFailed',
  DELETE_FAILED: 'legislativeNotices.deleteFailed',
  CONFIRM_DELETE: 'legislativeNotices.confirmDelete',
  TITLE_REQUIRED: 'legislativeNotices.titleRequired',
  CONTENT_REQUIRED: 'legislativeNotices.contentRequired',
  DEADLINE_DATE_REQUIRED: 'legislativeNotices.deadlineDateRequired',
  CREATE_TITLE: 'legislativeNotices.createTitle',
  EDIT_TITLE: 'legislativeNotices.editTitle',
  CATEGORIES: {
    KYOGYU: 'legislativeNotices.categories.KYOGYU',
    KYORYUNG: 'legislativeNotices.categories.KYORYUNG',
    YEGYU: 'legislativeNotices.categories.YEGYU',
  },
  DEADLINE_STATUS: {
    EXPIRED: 'legislativeNotices.deadlineStatus.expired',
    URGENT: 'legislativeNotices.deadlineStatus.urgent',
    NORMAL: 'legislativeNotices.deadlineStatus.normal',
  },
  ACTIONS: {
    BACK_TO_LIST: 'legislativeNotices.actions.backToList',
    CREATE: 'legislativeNotices.actions.create',
    EDIT: 'legislativeNotices.actions.edit',
    DELETE: 'legislativeNotices.actions.delete',
    APPROVE: 'legislativeNotices.actions.approve',
    UNAPPROVE: 'legislativeNotices.actions.unapprove',
  },
  DETAIL_SECTION: {
    TITLE: 'legislativeNotices.detail.title',
    LOADING: 'legislativeNotices.detail.loading',
    NOT_FOUND: 'legislativeNotices.detail.notFound',
    BACK_TO_LIST: 'legislativeNotices.detail.backToList',
    EDIT: 'legislativeNotices.detail.edit',
    DELETE: 'legislativeNotices.detail.delete',
    APPROVE: 'legislativeNotices.detail.approve',
    UNAPPROVE: 'legislativeNotices.detail.unapprove',
    CONFIRM_DELETE: 'legislativeNotices.detail.confirmDelete',
    APPROVE_SUCCESS: 'legislativeNotices.detail.approveSuccess',
    APPROVE_FAILED: 'legislativeNotices.detail.approveFailed',
    UNAPPROVE_SUCCESS: 'legislativeNotices.detail.unapproveSuccess',
    UNAPPROVE_FAILED: 'legislativeNotices.detail.unapproveFailed',
    DELETE_SUCCESS: 'legislativeNotices.detail.deleteSuccess',
    DELETE_FAILED: 'legislativeNotices.detail.deleteFailed',
    CONTENT: 'legislativeNotices.detail.content',
    LABELS: {
      AUTHOR: 'legislativeNotices.detail.labels.author',
      CREATED_AT: 'legislativeNotices.detail.labels.createdAt',
      UPDATED_AT: 'legislativeNotices.detail.labels.updatedAt',
      CATEGORY: 'legislativeNotices.detail.labels.category',
      DEADLINE_DATE: 'legislativeNotices.detail.labels.deadlineDate',
      DEADLINE_STATUS: 'legislativeNotices.detail.labels.deadlineStatus',
      VIEW_COUNT: 'legislativeNotices.detail.labels.viewCount',
    },
  },
} as const;

// 공포·공시 관련 키
export const PUBLICATIONS_KEYS = {
  TITLE: 'publications.title',
  LIST: 'publications.list',
  DETAIL: 'publications.detail',
  DESCRIPTION: 'publications.description',
  LOADING: 'publications.loading',
  NOT_FOUND: 'publications.notFound',
  LOAD_FAILED: 'publications.loadFailed',
  CREATE_SUCCESS: 'publications.createSuccess',
  UPDATE_SUCCESS: 'publications.updateSuccess',
  DELETE_SUCCESS: 'publications.deleteSuccess',
  SAVE_FAILED: 'publications.saveFailed',
  DELETE_FAILED: 'publications.deleteFailed',
  CONFIRM_DELETE: 'publications.confirmDelete',
  TITLE_REQUIRED: 'publications.titleRequired',
  CONTENT_REQUIRED: 'publications.contentRequired',
  CREATE_TITLE: 'publications.createTitle',
  EDIT_TITLE: 'publications.editTitle',
  CATEGORIES: {
    KYOGYU: 'publications.categories.KYOGYU',
    KYORYUNG: 'publications.categories.KYORYUNG',
    YEGYU: 'publications.categories.YEGYU',
  },
  ACTIONS: {
    BACK_TO_LIST: 'publications.actions.backToList',
    CREATE: 'publications.actions.create',
    EDIT: 'publications.actions.edit',
    DELETE: 'publications.actions.delete',
    APPROVE: 'publications.actions.approve',
    UNAPPROVE: 'publications.actions.unapprove',
  },
  DETAIL_SECTION: {
    TITLE: 'publications.detail.title',
    LOADING: 'publications.detail.loading',
    NOT_FOUND: 'publications.detail.notFound',
    BACK_TO_LIST: 'publications.detail.backToList',
    EDIT: 'publications.detail.edit',
    DELETE: 'publications.detail.delete',
    APPROVE: 'publications.detail.approve',
    UNAPPROVE: 'publications.detail.unapprove',
    CONFIRM_DELETE: 'publications.detail.confirmDelete',
    APPROVE_SUCCESS: 'publications.detail.approveSuccess',
    APPROVE_FAILED: 'publications.detail.approveFailed',
    UNAPPROVE_SUCCESS: 'publications.detail.unapproveSuccess',
    UNAPPROVE_FAILED: 'publications.detail.unapproveFailed',
    DELETE_SUCCESS: 'publications.detail.deleteSuccess',
    DELETE_FAILED: 'publications.detail.deleteFailed',
    DOWNLOAD_FAILED: 'publications.detail.downloadFailed',
    CONTENT: 'publications.detail.content',
    LABELS: {
      AUTHOR: 'publications.detail.labels.author',
      CREATED_AT: 'publications.detail.labels.createdAt',
      UPDATED_AT: 'publications.detail.labels.updatedAt',
      CATEGORY: 'publications.detail.labels.category',
      VIEW_COUNT: 'publications.detail.labels.viewCount',
    },
  },
} as const;



// 프로필 관련 키
export const PROFILE_KEYS = {
  TITLE: 'profile.title',
  DESCRIPTION: 'profile.description',
  LOADING: 'profile.loading',
  NOT_FOUND: 'profile.notFound',
  UPDATE_SUCCESS: 'profile.updateSuccess',
  UPDATE_FAILED: 'profile.updateFailed',
  PASSWORD_CHANGE_SUCCESS: 'profile.passwordChangeSuccess',
  PASSWORD_CHANGE_FAILED: 'profile.passwordChangeFailed',
  LOGIN_REQUIRED: 'profile.loginRequired',
  LOGIN_REQUIRED_DESC: 'profile.loginRequiredDesc',
  LABELS: {
    USERNAME: 'profile.labels.username',
    EMAIL: 'profile.labels.email',
    ORGAN_NAME: 'profile.labels.organName',
    BUBMYENG: 'profile.labels.bubmyeng',
    ROLE: 'profile.labels.role',
    CREATED_AT: 'profile.labels.createdAt',
    UPDATED_AT: 'profile.labels.updatedAt',
  },
  ACTIONS: {
    UPDATE_PROFILE: 'profile.actions.updateProfile',
    CHANGE_PASSWORD: 'profile.actions.changePassword',
    BACK_TO_HOME: 'profile.actions.backToHome',
  },
} as const;

// 페이지네이션 관련 키
export const PAGINATION_KEYS = {
  SHOW: 'pagination.show',
  ENTRIES: 'pagination.entries',
  OF: 'pagination.of',
  TO: 'pagination.to',
  FROM: 'pagination.from',
  PREVIOUS: 'pagination.previous',
  NEXT: 'pagination.next',
  FIRST: 'pagination.first',
  LAST: 'pagination.last',
  PAGE: 'pagination.page',
  PAGES: 'pagination.pages',
} as const;

// 모든 키를 하나의 객체로 통합
export const TRANSLATION_KEYS = {
  COMMON: COMMON_KEYS,
  AUTH: AUTH_KEYS,
  NAVIGATION: NAVIGATION_KEYS,
  LAWS: LAWS_KEYS,
  ANNOUNCEMENTS: ANNOUNCEMENTS_KEYS,
  LEGISLATIVE_NOTICES: LEGISLATIVE_NOTICES_KEYS,
  PUBLICATIONS: PUBLICATIONS_KEYS,
  PROFILE: PROFILE_KEYS,
  PAGINATION: PAGINATION_KEYS,
} as const;

// 타입 정의
export type TranslationKey = typeof TRANSLATION_KEYS[keyof typeof TRANSLATION_KEYS][keyof typeof TRANSLATION_KEYS[keyof typeof TRANSLATION_KEYS]]; 